import React, { useEffect, useState } from 'react';
import { SlotCard } from '../types/types';
import { CARD_ICONS } from '../utils/cardConstants';
import '../styles/Slot.css';

// Import all HP icons
import glassesIcon from '../assets/hp_icons/glasses.png';
import hatIcon from '../assets/hp_icons/hat.png';
import scrollIcon from '../assets/hp_icons/scroll.png';
import owlIcon from '../assets/hp_icons/owl.png';
import letterIcon from '../assets/hp_icons/letter.png';
import timeturnerIcon from '../assets/hp_icons/timeturner.png';
import wandIcon from '../assets/hp_icons/wand.png';
import beerIcon from '../assets/hp_icons/beer.png';
import bookIcon from '../assets/hp_icons/book.png';
import goldenSnitchIcon from '../assets/hp_icons/golden-snitch.png';
import cauldronIcon from '../assets/hp_icons/cauldron.png';
import scarfIcon from '../assets/hp_icons/scarf.png';
import hogwartsCrestIcon from '../assets/hp_icons/hogwarts-crest.png';
import potionIcon from '../assets/hp_icons/potion.png';

// Map icon types to their image files
const iconImages: Record<string, string> = {
  'glasses': glassesIcon,
  'hat': hatIcon,
  'scroll': scrollIcon,
  'owl': owlIcon,
  'letter': letterIcon,
  'timeturner': timeturnerIcon,
  'wand': wandIcon,
  'beer': beerIcon,
  'book': bookIcon,
  'golden-snitch': goldenSnitchIcon,
  'cauldron': cauldronIcon,
  'scarf': scarfIcon,
  'hogwarts-crest': hogwartsCrestIcon,
  'potion': potionIcon
};

interface InvisibleSlotProps {
  cards: SlotCard[];
  onCardClick?: (card: SlotCard) => void;
}

const InvisibleSlot: React.FC<InvisibleSlotProps> = ({ cards = [], onCardClick }) => {
  const cardsArray = Array.isArray(cards) ? cards : [];
  const [animatingCardId, setAnimatingCardId] = useState<string | null>(null);
  const [animationInProgress, setAnimationInProgress] = useState(false);
  
  // Fixed positions for the three card slots
  const fixedPositions = [
    { left: "calc(50% - 120px)" },
    { left: "calc(50% - 35px)" },
    { left: "calc(50% + 50px)" }
  ];
  
  // Debugging log
  console.log('Rendering invisible slot with cards:', cardsArray.map(c => c.id));
  
  // Effect to make cards clickable
  useEffect(() => {
    setTimeout(() => {
      const cardElements = document.querySelectorAll('.invisible-slot-card');
      cardElements.forEach((element) => {
        // Check if card is not already clickable
        if (!element.classList.contains('clickable')) {
          element.classList.add('clickable');
        }
      });
    }, 100);
  }, [cardsArray]);

  // Handle card click with animation
  const handleCardClick = (card: SlotCard, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Prevent multiple clicks during animation
    if (animationInProgress) {
      return;
    }
    
    console.log('Invisible slot card clicked:', card.id);
    
    // Set animating state only for this specific card
    setAnimatingCardId(card.id);
    setAnimationInProgress(true);
    
    // Apply animation class only to the clicked card
    const clickedElement = e.currentTarget as HTMLElement;
    clickedElement.classList.add('card-being-moved');
    
    // Call the click handler after a short delay to start the animation
    setTimeout(() => {
      if (onCardClick) {
        onCardClick(card);
      }
      
      // Reset animation state after animation completes
      setTimeout(() => {
        setAnimationInProgress(false);
        setAnimatingCardId(null);
      }, 300);
    }, 150);
  };

  // Create a static container with absolute positioned cards at fixed positions
  return (
    <div className="slot-container invisible-slot-container">
      <div className="invisible-card-wrapper" style={{ position: 'relative', width: '100%', height: '90px' }}>
        {cardsArray.map((card, index) => {
          if (index >= 3) return null; // Only show up to 3 cards
          
          return (
            <div 
              key={`card-${card.id}`}
              className={`slot-card invisible-slot-card ${animatingCardId === card.id ? 'card-being-moved' : ''}`}
              data-card-id={card.id}
              data-card-type={card.type}
              data-position={index}
              onClick={(e) => handleCardClick(card, e)}
              style={{
                position: 'absolute',
                top: '0',
                ...fixedPositions[index],
                margin: '0',
                transition: animatingCardId === card.id ? undefined : 'none !important',
                transform: animatingCardId === card.id ? undefined : 'none !important',
                width: '70px',
                height: '70px'
              }}
            >
              <div className={`slot-card-icon ${CARD_ICONS[card.type % CARD_ICONS.length]}`}>
                <img 
                  src={iconImages[CARD_ICONS[card.type % CARD_ICONS.length]]} 
                  alt={CARD_ICONS[card.type % CARD_ICONS.length]} 
                  className="slot-card-icon-image" 
                />
              </div>
            </div>
          );
        })}
        
        {/* Render fixed empty slots that are not filled */}
        {[0, 1, 2].map(index => {
          // Only render empty slots for positions where there is no card
          if (index < cardsArray.length) return null;
          
          return (
            <div 
              key={`empty-${index}`}
              className="empty-slot invisible-empty-slot"
              style={{
                position: 'absolute',
                top: '0',
                ...fixedPositions[index],
                margin: '0',
                width: '70px',
                height: '70px'
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default InvisibleSlot; 